import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, API, fmtTime, fmtDateTime, fmtDuration, errMsg, TOKEN_KEY } from "@/lib/api";
import { StatusPill, ConfBadge, Empty, Skeletons, SectionHead } from "@/components/Primitives";
import { MeetingChat } from "@/components/MeetingChat";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  ArrowLeft, UploadCloud, Sparkles, Loader2, Download, Radio, Mic2, ListChecks, FileText,
  Users, AlertTriangle, CheckCircle2, CalendarClock, Play, Search, Pencil, UserPlus,
  SmilePlus, Meh, Frown, Zap, TrendingUp, TrendingDown, AlertCircle, Lightbulb,
  Languages, ChevronDown, Copy, Check,
} from "lucide-react";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ParticipantPicker } from "@/components/ParticipantPicker";

const EXPORTS = [["pdf", "PDF"], ["docx", "DOCX"], ["txt", "TXT"], ["csv", "CSV"], ["json", "JSON"]];

// supported languages (mirrors backend AI.SUPPORTED_LANGUAGES)
const LANGUAGES = [
  { code: "en", label: "English",             flag: "🇬🇧" },
  { code: "ar", label: "Arabic",              flag: "🇸🇦" },
  { code: "zh", label: "Chinese (Simplified)",flag: "🇨🇳" },
  { code: "ja", label: "Japanese",            flag: "🇯🇵" },
  { code: "ko", label: "Korean",              flag: "🇰🇷" },
  { code: "fr", label: "French",              flag: "🇫🇷" },
  { code: "de", label: "German",              flag: "🇩🇪" },
  { code: "es", label: "Spanish",             flag: "🇪🇸" },
  { code: "pt", label: "Portuguese",          flag: "🇵🇹" },
  { code: "nl", label: "Dutch",               flag: "🇳🇱" },
  { code: "th", label: "Thai",                flag: "🇹🇭" },
  { code: "ms", label: "Malay",               flag: "🇲🇾" },
  { code: "hi", label: "Hindi",               flag: "🇮🇳" },
  { code: "ru", label: "Russian",             flag: "🇷🇺" },
  { code: "tr", label: "Turkish",             flag: "🇹🇷" },
  { code: "vi", label: "Vietnamese",          flag: "🇻🇳" },
  { code: "it", label: "Italian",             flag: "🇮🇹" },
];

// sentiment helpers
const SENTIMENT_META = {
  sangat_positif: { label: "Sangat Positif", color: "text-emerald-600", bg: "bg-emerald-50", border: "border-emerald-200", bar: "bg-emerald-500", icon: SmilePlus },
  positif:        { label: "Positif",        color: "text-emerald-500", bg: "bg-emerald-50", border: "border-emerald-200", bar: "bg-emerald-400", icon: SmilePlus },
  netral:         { label: "Netral",         color: "text-slate-500",   bg: "bg-slate-50",   border: "border-slate-200",   bar: "bg-slate-400",   icon: Meh       },
  tegang:         { label: "Tegang",         color: "text-amber-600",   bg: "bg-amber-50",   border: "border-amber-200",   bar: "bg-amber-500",   icon: Zap       },
  negatif:        { label: "Negatif",        color: "text-red-600",     bg: "bg-red-50",     border: "border-red-200",     bar: "bg-red-500",     icon: Frown     },
};

export default function MeetingDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [m, setM] = useState(null);
  const [segs, setSegs] = useState([]);
  const [summary, setSummary] = useState(null);
  const [sentiment, setSentiment] = useState(null);
  const [analyzingSentiment, setAnalyzingSentiment] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  // translation state
  const [selectedLang, setSelectedLang] = useState("en");
  const [translation, setTranslation] = useState(null);      // { segments, target_lang_name, ... }
  const [translating, setTranslating] = useState(false);
  const [translationView, setTranslationView] = useState("bilingual"); // bilingual | translated
  const [copied, setCopied] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [summarizing, setSummarizing] = useState(false);
  const [q, setQ] = useState("");
  const [speaker, setSpeaker] = useState("all");
  const [pplOpen, setPplOpen] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [picked, setPicked] = useState([]);
  const [savingPpl, setSavingPpl] = useState(false);
  const fileRef = useRef(null);
  const audioRef = useRef(null);

  const load = async () => {
    try {
      const [mm, tt, ii] = await Promise.all([
        api.get(`/meetings/${id}`),
        api.get(`/meetings/${id}/transcript`),
        api.get("/action-items", { params: { meeting_id: id } }),
      ]);
      setM(mm.data); setSegs(tt.data); setItems(ii.data);
      try { const { data } = await api.get(`/meetings/${id}/summary`); setSummary(data); } catch { setSummary(null); }
      try { const { data } = await api.get(`/meetings/${id}/sentiment`); setSentiment(data); } catch { setSentiment(null); }
      // pre-load English translation if already exists
      try { const { data } = await api.get(`/meetings/${id}/translate?lang=en`); setTranslation(data); } catch { setTranslation(null); }
    } catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/meetings/${id}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 300000 });
      toast.success(`Transkripsi selesai — ${data.segments} segmen, akurasi ${(data.accuracy * 100).toFixed(1)}%`);
      await load();
    } catch (e) { toast.error(errMsg(e)); } finally { setUploading(false); }
  };

  const genSummary = async () => {
    setSummarizing(true);
    try {
      const { data } = await api.post(`/meetings/${id}/summary`);
      setSummary(data.summary); setItems(data.action_items);
      toast.success(`AI Summary dibuat · ${data.action_items.length} action items`);
    } catch (e) { toast.error(errMsg(e)); } finally { setSummarizing(false); }
  };

  const genSentiment = async () => {
    setAnalyzingSentiment(true);
    try {
      const { data } = await api.post(`/meetings/${id}/sentiment`);
      setSentiment(data);
      toast.success("Analisis sentimen selesai");
    } catch (e) { toast.error(errMsg(e)); } finally { setAnalyzingSentiment(false); }
  };

  const loadTranslation = async (lang) => {
    try {
      const { data } = await api.get(`/meetings/${id}/translate?lang=${lang}`);
      setTranslation(data);
    } catch { setTranslation(null); }
  };

  const handleLangChange = async (lang) => {
    setSelectedLang(lang);
    setTranslation(null);
    await loadTranslation(lang);
  };

  const runTranslation = async () => {
    setTranslating(true);
    try {
      const { data } = await api.post(`/meetings/${id}/translate`, { target_lang: selectedLang });
      setTranslation(data);
      toast.success(`Terjemahan ke ${LANGUAGES.find(l => l.code === selectedLang)?.label} selesai`);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setTranslating(false); }
  };

  const copyTranslation = async () => {
    if (!translation?.segments) return;
    const text = translation.segments
      .map(s => `[${fmtTime(s.start || 0)}] ${s.speaker_name || s.speaker || "?"}: ${s.translated_text}`)
      .join("\n");
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const exportFile = async (fmt) => {
    try {
      const res = await api.get(`/meetings/${id}/export/${fmt}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${(m?.title || "meeting").replace(/\W+/g, "_")}.${fmt}`;
      a.click(); URL.revokeObjectURL(url);
      toast.success(`Export ${fmt.toUpperCase()} berhasil`);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const updateItem = async (itemId, patch) => {
    try {
      const { data } = await api.patch(`/action-items/${itemId}`, patch);
      setItems((p) => p.map((x) => (x.id === itemId ? data : x)));
      toast.success("Action item diperbarui");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const seek = (s) => {
    if (audioRef.current) { audioRef.current.currentTime = s; audioRef.current.play(); }
    else toast.info("Audio rekaman belum tersedia untuk rapat ini.");
  };

  const openParticipants = async () => {
    setPicked(m?.participant_ids || []);
    setPplOpen(true);
    if (allUsers.length === 0) {
      try { const { data } = await api.get("/users"); setAllUsers(data); }
      catch (e) { toast.error(errMsg(e)); }
    }
  };

  const saveParticipants = async () => {
    setSavingPpl(true);
    try {
      const { data } = await api.post(`/meetings/${id}/participants`, { participant_ids: picked });
      setM(data);
      setPplOpen(false);
      toast.success(`${data.participant_count} peserta disimpan`);
    } catch (e) { toast.error(errMsg(e)); } finally { setSavingPpl(false); }
  };

  if (loading) return <Skeletons rows={4} className="h-32" />;
  if (!m) return <Empty icon={AlertTriangle} title="Rapat tidak ditemukan" sub="Data mungkin sudah dihapus." />;

  const speakers = [...new Set(segs.map((s) => s.speaker_name))];
  const shown = segs.filter((s) =>
    (speaker === "all" || s.speaker_name === speaker) && (!q || s.text.toLowerCase().includes(q.toLowerCase()))
  );
  const audioSrc = m.audio_file ? `${API}/meetings/${id}/audio?t=${localStorage.getItem(TOKEN_KEY) ? "1" : "0"}` : null;

  return (
    <div className="space-y-7" data-testid="meeting-detail-page">
      <div>
        <Button variant="ghost" size="sm" className="mb-3 -ml-2 text-slate-500" onClick={() => nav("/meetings")} data-testid="back-btn">
          <ArrowLeft size={15} className="mr-1.5" /> Kembali ke daftar
        </Button>
        <div className="flex flex-wrap items-start justify-between gap-5">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-3">
              <StatusPill status={m.status} />
              {m.tags?.map((t) => (
                <span key={t} className="rounded-full bg-slate-100 px-2.5 py-1 text-xs text-slate-600">#{t}</span>
              ))}
            </div>
            <h1 className="mt-3 font-display text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900">{m.title}</h1>
            <p className="mt-2.5 text-sm text-slate-500">
              {fmtDateTime(m.scheduled_at)} · {m.location || "—"} · {m.division || "—"} · Host {m.host_name}
            </p>
          </div>
          <div className="flex flex-wrap gap-2.5">
            <input ref={fileRef} type="file" accept="audio/*,video/mp4" hidden onChange={(e) => upload(e.target.files?.[0])} data-testid="audio-file-input" />
            <Button variant="outline" className="h-11 rounded-xl border-slate-200" onClick={() => nav(`/meetings/${id}/edit`)} data-testid="edit-meeting-btn">
              <Pencil size={16} className="mr-2" /> Edit
            </Button>
            <Button variant="outline" className="h-11 rounded-xl border-slate-200" disabled={uploading} onClick={() => fileRef.current?.click()} data-testid="upload-audio-btn">
              {uploading ? <><Loader2 size={16} className="mr-2 animate-spin" /> Transkripsi…</> : <><UploadCloud size={16} className="mr-2" /> Upload Audio</>}
            </Button>
            <Button variant="outline" className="h-11 rounded-xl border-slate-200" onClick={() => nav(`/live/${id}`)} data-testid="start-live-btn">
              <Radio size={16} className="mr-2 text-brand-600" /> Live Session
            </Button>
            <Button className="h-11 rounded-xl grad-brand text-white px-5" onClick={genSummary} disabled={summarizing || segs.length === 0} data-testid="generate-summary-btn">
              {summarizing ? <><Loader2 size={16} className="mr-2 animate-spin" /> Menganalisis…</> : <><Sparkles size={16} className="mr-2" /> Generate AI Summary</>}
            </Button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          ["Segmen Transkrip", segs.length, FileText],
          ["Durasi", m.duration_seconds ? fmtDuration(m.duration_seconds) : "—", CalendarClock],
          ["Akurasi ASR", m.accuracy ? `${(m.accuracy * 100).toFixed(1)}%` : "—", Mic2],
          ["Action Items", items.length, ListChecks],
        ].map(([label, value, Icon], i) => (
          <div key={label} className="clay p-5 animate-fade-up" style={{ animationDelay: `${i * 60}ms` }} data-testid={`detail-stat-${i}`}>
            <div className="flex items-center gap-2 text-slate-400"><Icon size={14} /><span className="label-xs">{label}</span></div>
            <p className="mt-2.5 font-display text-2xl font-semibold text-slate-900">{value}</p>
          </div>
        ))}
      </div>

      {audioSrc && (
        <div className="clay p-5" data-testid="audio-player-card">
          <p className="label-xs mb-3">Recording</p>
          <audio ref={audioRef} src={audioSrc} controls className="w-full" data-testid="meeting-audio-player" />
        </div>
      )}

      <Tabs defaultValue="transcript">
        <TabsList className="rounded-xl bg-slate-100/80 p-1" data-testid="detail-tabs">
          <TabsTrigger value="transcript" className="rounded-lg" data-testid="tab-transcript">Transcript</TabsTrigger>
          <TabsTrigger value="summary" className="rounded-lg" data-testid="tab-summary">AI Summary</TabsTrigger>
          <TabsTrigger value="sentiment" className="rounded-lg" data-testid="tab-sentiment">Sentimen</TabsTrigger>
          <TabsTrigger value="translation" className="rounded-lg" data-testid="tab-translation">Terjemahan</TabsTrigger>
          <TabsTrigger value="actions" className="rounded-lg" data-testid="tab-actions">Action Items</TabsTrigger>
          <TabsTrigger value="chat" className="rounded-lg" data-testid="tab-chat">AI Chat</TabsTrigger>
          <TabsTrigger value="participants" className="rounded-lg" data-testid="tab-participants">Participants</TabsTrigger>
          <TabsTrigger value="export" className="rounded-lg" data-testid="tab-export">Export</TabsTrigger>
        </TabsList>

        <TabsContent value="transcript" className="mt-6">
          <div className="clay p-6" data-testid="transcript-panel">
            <div className="flex flex-wrap gap-3 mb-6">
              <div className="relative flex-1 min-w-[220px]">
                <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari dalam transkrip…" className="h-10 pl-10 rounded-xl" data-testid="transcript-search-input" />
              </div>
              <Select value={speaker} onValueChange={setSpeaker}>
                <SelectTrigger className="h-10 w-[200px] rounded-xl" data-testid="transcript-speaker-filter"><SelectValue placeholder="Semua speaker" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Semua Speaker</SelectItem>
                  {speakers.map((sp) => <SelectItem key={sp} value={sp}>{sp}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {shown.length === 0 ? (
              <Empty icon={Mic2} title="Belum ada transkrip" sub="Unggah rekaman audio atau jalankan sesi live untuk menghasilkan transkrip otomatis."
                action={<Button className="rounded-xl grad-brand text-white" onClick={() => fileRef.current?.click()}>Upload Audio</Button>} />
            ) : (
              <div className="space-y-1" data-testid="transcript-timeline">
                {shown.map((s, i) => (
                  <button key={s.id} onClick={() => seek(s.start)}
                    className="group w-full text-left flex gap-4 rounded-xl border-l-2 border-slate-100 px-4 py-3.5 hover:border-brand-600 hover:bg-brand-50/40 transition-colors animate-fade-up"
                    style={{ animationDelay: `${Math.min(i * 25, 400)}ms` }} data-testid={`transcript-row-${s.seq}`}>
                    <span className="shrink-0 font-mono text-xs text-slate-400 pt-0.5 w-[68px]">{fmtTime(s.start)}</span>
                    <span className="min-w-0 flex-1">
                      <span className="flex flex-wrap items-center gap-2">
                        <span className={`text-sm font-semibold ${s.is_unknown ? "text-amber-600" : "text-slate-800"}`}>{s.speaker_name}</span>
                        <span className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[10px] text-slate-500">{s.speaker_label}</span>
                        <ConfBadge value={s.confidence} label="ASR " />
                        <ConfBadge value={s.voice_confidence} label="Voice " />
                      </span>
                      <span className="mt-1.5 block text-sm leading-relaxed text-slate-700">{s.text}</span>
                    </span>
                    <Play size={14} className="mt-1 shrink-0 text-slate-300 group-hover:text-brand-600 transition-colors" />
                  </button>
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="summary" className="mt-6">
          {!summary ? (
            <div className="clay">
              <Empty icon={Sparkles} title="Ringkasan AI belum dibuat"
                sub="Jalankan analisis untuk menghasilkan executive summary, keputusan, risiko, dan action items secara otomatis."
                action={<Button className="rounded-xl grad-brand text-white" onClick={genSummary} disabled={segs.length === 0 || summarizing}>
                  {summarizing ? "Menganalisis…" : "Generate AI Summary"}</Button>} />
            </div>
          ) : (
            <div className="grid gap-6 lg:grid-cols-3" data-testid="summary-panel">
              <div className="clay p-7 lg:col-span-2">
                <div className="flex items-center gap-2 mb-4">
                  <span className="grid place-items-center h-8 w-8 rounded-xl grad-brand text-white"><Sparkles size={15} /></span>
                  <div>
                    <p className="label-xs">Executive Summary</p>
                    <p className="text-[11px] text-slate-400 font-mono">{summary.model}</p>
                  </div>
                </div>
                <p className="text-base leading-relaxed text-slate-700" data-testid="executive-summary-text">{summary.executive_summary}</p>
                {[["Agenda", summary.agenda, CalendarClock], ["Pembahasan", summary.discussion, FileText],
                  ["Keputusan", summary.decisions, CheckCircle2], ["Risiko", summary.risks, AlertTriangle]].map(([title, list, Icon]) =>
                  (list?.length ? (
                    <div key={title} className="mt-7">
                      <div className="flex items-center gap-2 mb-3"><Icon size={14} className="text-brand-600" /><p className="label-xs">{title}</p></div>
                      <ul className="space-y-2.5">
                        {list.map((v, i) => (
                          <li key={i} className="flex gap-3 text-sm leading-relaxed text-slate-700">
                            <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full grad-brand" />{v}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : null))}
              </div>
              <div className="space-y-6">
                <div className="clay p-6">
                  <p className="label-xs">Kesimpulan Rapat</p>
                  <p className="mt-3 text-sm leading-relaxed text-slate-700">{summary.conclusion || "—"}</p>
                </div>
                <div className="clay p-6">
                  <p className="label-xs">Rapat Berikutnya</p>
                  <p className="mt-3 text-sm leading-relaxed text-slate-700">{summary.next_meeting || "Belum ditentukan"}</p>
                </div>
                <div className="clay p-6">
                  <p className="label-xs">Sentimen</p>
                  <p className="mt-3 text-sm font-semibold capitalize text-slate-800">{summary.sentiment}</p>
                  <div className="mt-3 h-2 rounded-full bg-slate-100 overflow-hidden">
                    <div className={`h-full rounded-full ${summary.sentiment === "positive" ? "bg-emerald-500 w-[85%]" : summary.sentiment === "negative" ? "bg-red-400 w-[30%]" : "grad-brand w-[58%]"}`} />
                  </div>
                </div>
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="actions" className="mt-6">
          <div className="clay p-6" data-testid="actions-panel">
            <SectionHead title="Action Items" sub="Diekstraksi otomatis oleh AI dari transkrip rapat" />
            {items.length === 0 ? (
              <Empty icon={ListChecks} title="Belum ada action item" sub="Action items akan muncul otomatis setelah AI Summary dijalankan." />
            ) : (
              <div className="grid gap-4 md:grid-cols-2">
                {items.map((it) => (
                  <div key={it.id} className="rounded-2xl border border-slate-200/70 p-5 hover:shadow-clay transition-shadow" data-testid={`action-item-${it.id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <p className="text-sm font-semibold leading-snug text-slate-800">{it.task}</p>
                      <StatusPill status={it.priority} />
                    </div>
                    <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2 text-xs text-slate-500">
                      <span className="inline-flex items-center gap-1.5"><Users size={13} /> PIC: <b className="text-slate-700 font-medium">{it.owner}</b></span>
                      <span className="inline-flex items-center gap-1.5"><CalendarClock size={13} /> {it.deadline}</span>
                    </div>
                    <div className="mt-4">
                      <div className="flex items-center justify-between text-xs text-slate-500 mb-1.5">
                        <span>Progress</span><span className="font-mono">{it.progress}%</span>
                      </div>
                      <Progress value={it.progress} className="h-1.5" />
                    </div>
                    <div className="mt-4 flex items-center gap-2">
                      <Select value={it.status} onValueChange={(v) => updateItem(it.id, { status: v, progress: v === "done" ? 100 : v === "in_progress" ? 50 : 0 })}>
                        <SelectTrigger className="h-9 rounded-xl text-xs" data-testid={`action-status-${it.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="open">Open</SelectItem>
                          <SelectItem value="in_progress">Berjalan</SelectItem>
                          <SelectItem value="done">Selesai</SelectItem>
                        </SelectContent>
                      </Select>
                      <StatusPill status={it.status} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="chat" className="mt-6">
          <MeetingChat meetingId={id} hasTranscript={segs.length > 0} />
        </TabsContent>

        <TabsContent value="participants" className="mt-6">
          <div className="clay p-6" data-testid="participants-panel">
            <SectionHead title="Peserta Rapat" sub="Status voiceprint menentukan akurasi pengenalan speaker"
              right={<Button variant="outline" className="rounded-xl border-slate-200" onClick={openParticipants} data-testid="manage-participants-btn">
                <UserPlus size={15} className="mr-2 text-brand-600" /> Kelola Peserta
              </Button>} />
            {m.participants?.length === 0 ? (
              <Empty icon={Users} title="Belum ada peserta" sub="Tambahkan peserta melalui tombol Kelola Peserta."
                action={<Button className="rounded-xl grad-brand text-white" onClick={openParticipants} data-testid="empty-manage-participants-btn">Kelola Peserta</Button>} />
            ) : (
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {m.participants.map((p) => {
                  const spoken = segs.filter((s) => s.speaker_name === p.name);
                  return (
                    <div key={p.user_id} className="rounded-2xl border border-slate-200/70 p-5" data-testid={`speaker-card-${p.user_id}`}>
                      <div className="flex items-center gap-3">
                        <span className="grid place-items-center h-11 w-11 rounded-full grad-brand text-white text-xs font-semibold">{p.name.slice(0, 2).toUpperCase()}</span>
                        <div className="min-w-0">
                          <p className="truncate text-sm font-semibold text-slate-800">{p.name}</p>
                          <p className="text-xs text-slate-500">{p.enrolled ? "Voiceprint aktif" : "Belum enroll"}</p>
                        </div>
                      </div>
                      <div className="mt-4 flex items-center justify-between text-xs text-slate-500">
                        <span>{spoken.length} segmen</span>
                        {p.enrolled ? <span className="inline-flex items-center gap-1 text-emerald-600"><Mic2 size={13} /> Terdaftar</span>
                          : <span className="inline-flex items-center gap-1 text-amber-600"><AlertTriangle size={13} /> Perlu enroll</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="export" className="mt-6">
          <div className="clay p-7" data-testid="export-panel">
            <SectionHead title="Export Meeting Report" sub="Semua dokumen diberi watermark identitas pengguna & timestamp" />
            <div className="grid gap-4 sm:grid-cols-3 lg:grid-cols-5">
              {EXPORTS.map(([fmt, label]) => (
                <button key={fmt} onClick={() => exportFile(fmt)}
                  className="group rounded-2xl border border-slate-200/70 p-6 text-center hover:border-brand-600 hover:bg-brand-50/40 transition-colors"
                  data-testid={`export-${fmt}-btn`}>
                  <Download size={20} className="mx-auto text-slate-400 group-hover:text-brand-600 transition-colors" />
                  <p className="mt-3 font-display text-sm font-semibold text-slate-800">{label}</p>
                </button>
              ))}
            </div>
          </div>
        </TabsContent>

        {/* ── Translation tab ── */}
        <TabsContent value="translation" className="mt-6" data-testid="translation-panel">
          {/* Language selector + controls */}
          <div className="clay p-5 mb-5">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2 shrink-0">
                <Languages size={17} className="text-brand-600" />
                <p className="text-sm font-semibold text-slate-800">Terjemahkan ke:</p>
              </div>
              <Select value={selectedLang} onValueChange={handleLangChange} data-testid="lang-select">
                <SelectTrigger className="w-52 h-9 rounded-xl border-slate-200 text-sm" data-testid="lang-select-trigger">
                  <SelectValue>
                    {(() => { const l = LANGUAGES.find(x => x.code === selectedLang); return l ? `${l.flag} ${l.label}` : selectedLang; })()}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {LANGUAGES.map(l => (
                    <SelectItem key={l.code} value={l.code} data-testid={`lang-option-${l.code}`}>
                      {l.flag} {l.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                className="h-9 px-5 rounded-xl grad-brand text-white text-sm ml-auto"
                onClick={runTranslation}
                disabled={translating || segs.length === 0}
                data-testid="run-translation-btn"
              >
                {translating
                  ? <><Loader2 size={14} className="mr-2 animate-spin" /> Menerjemahkan…</>
                  : <><Languages size={14} className="mr-2" /> {translation ? "Perbarui" : "Terjemahkan"}</>
                }
              </Button>
              {translation && (
                <>
                  <div className="flex rounded-xl border border-slate-200 overflow-hidden">
                    <button
                      className={`px-3 py-1.5 text-xs font-medium transition-colors ${translationView === "bilingual" ? "bg-brand-600 text-white" : "bg-white text-slate-500 hover:bg-slate-50"}`}
                      onClick={() => setTranslationView("bilingual")}
                      data-testid="view-bilingual-btn"
                    >Bilingual</button>
                    <button
                      className={`px-3 py-1.5 text-xs font-medium transition-colors ${translationView === "translated" ? "bg-brand-600 text-white" : "bg-white text-slate-500 hover:bg-slate-50"}`}
                      onClick={() => setTranslationView("translated")}
                      data-testid="view-translated-btn"
                    >Terjemahan Saja</button>
                  </div>
                  <Button variant="ghost" size="sm" className="h-9 rounded-xl border border-slate-200 text-xs" onClick={copyTranslation} data-testid="copy-translation-btn">
                    {copied ? <><Check size={13} className="mr-1.5 text-emerald-600" /> Disalin!</> : <><Copy size={13} className="mr-1.5" /> Salin</>}
                  </Button>
                </>
              )}
            </div>
            {segs.length === 0 && (
              <p className="mt-3 text-xs text-amber-600 flex items-center gap-1.5">
                <AlertCircle size={12} /> Upload audio atau lakukan live meeting terlebih dahulu sebelum menerjemahkan.
              </p>
            )}
            {translation && (
              <p className="mt-2 text-xs text-slate-400">
                Terakhir diterjemahkan: {fmtDateTime(translation.created_at)} · {translation.segment_count} segmen · {translation.source_lang?.toUpperCase()} → {translation.target_lang?.toUpperCase()}
              </p>
            )}
          </div>

          {/* Translated content */}
          {translating ? (
            <div className="clay p-12 text-center" data-testid="translation-loading">
              <Loader2 size={32} className="mx-auto animate-spin text-brand-600 mb-3" />
              <p className="text-sm font-medium text-slate-600">Menerjemahkan {segs.length} segmen…</p>
              <p className="text-xs text-slate-400 mt-1">Ini mungkin membutuhkan beberapa detik. Jangan tutup halaman ini.</p>
            </div>
          ) : translation ? (
            <div className="space-y-2" data-testid="translation-result">
              {translation.segments?.map((seg, i) => {
                const speaker = seg.speaker_name || seg.speaker || "Unknown";
                const initials = speaker.slice(0, 2).toUpperCase();
                return (
                  <div key={i} className="clay p-4 hover:shadow-md transition-shadow" data-testid={`trans-seg-${i}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="grid place-items-center h-6 w-6 shrink-0 rounded-full grad-brand text-white text-[10px] font-bold">{initials}</span>
                      <span className="text-xs font-semibold text-brand-700">{speaker}</span>
                      <span className="text-[10px] text-slate-400 font-mono">{fmtTime(seg.start || 0)}</span>
                    </div>
                    {translationView === "bilingual" ? (
                      <div className="grid grid-cols-2 gap-3">
                        <div className="rounded-lg bg-slate-50 border border-slate-100 p-3">
                          <p className="text-[9px] font-bold uppercase tracking-wider text-slate-400 mb-1.5">{(translation.source_lang || "ID").toUpperCase()} — ASLI</p>
                          <p className="text-sm text-slate-700 leading-relaxed">{seg.text}</p>
                        </div>
                        <div className="rounded-lg bg-blue-50 border border-blue-100 p-3">
                          <p className="text-[9px] font-bold uppercase tracking-wider text-blue-400 mb-1.5">
                            {translation.target_lang?.toUpperCase()} — {translation.target_lang_name?.toUpperCase()}
                          </p>
                          <p className="text-sm text-blue-900 leading-relaxed">{seg.translated_text}</p>
                        </div>
                      </div>
                    ) : (
                      <p className="text-sm text-slate-800 leading-relaxed">{seg.translated_text}</p>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="clay p-12 text-center" data-testid="translation-empty">
              <Languages size={36} className="mx-auto text-slate-300 mb-3" />
              <p className="font-medium text-slate-600">Pilih bahasa dan klik Terjemahkan</p>
              <p className="text-sm text-slate-400 mt-1">
                {LANGUAGES.find(l => l.code === selectedLang)?.flag} Mendukung {LANGUAGES.length} bahasa
              </p>
            </div>
          )}
        </TabsContent>

        {/* ── Sentiment Analysis tab ── */}
        <TabsContent value="sentiment" className="mt-6" data-testid="sentiment-panel">
          {!sentiment ? (
            <div className="clay p-10 text-center">
              {segs.length === 0 ? (
                <>
                  <AlertCircle size={36} className="mx-auto text-slate-300 mb-3" />
                  <p className="font-medium text-slate-600">Belum ada transkrip</p>
                  <p className="text-sm text-slate-400 mt-1">Upload audio atau lakukan live meeting terlebih dahulu sebelum menganalisis sentimen.</p>
                </>
              ) : (
                <>
                  <SmilePlus size={36} className="mx-auto text-slate-300 mb-3" />
                  <p className="font-medium text-slate-600">Laporan sentimen belum dibuat</p>
                  <p className="text-sm text-slate-400 mt-1 mb-5">AI akan menganalisis nada bicara dan kata-kata dari {segs.length} segmen transkrip.</p>
                  <Button className="h-10 px-7 rounded-xl grad-brand text-white" onClick={genSentiment} disabled={analyzingSentiment} data-testid="gen-sentiment-btn">
                    {analyzingSentiment ? <><Loader2 size={15} className="mr-2 animate-spin" /> Menganalisis…</> : <><Sparkles size={15} className="mr-2" /> Analisis Sentimen</>}
                  </Button>
                </>
              )}
            </div>
          ) : (() => {
            const meta = SENTIMENT_META[sentiment.overall] || SENTIMENT_META.netral;
            const ScoreIcon = meta.icon;
            const pct = Math.round(((parseFloat(sentiment.score) + 1) / 2) * 100);
            return (
              <div className="space-y-5">
                {/* Header banner */}
                <div className={`clay p-6 ${meta.bg} border ${meta.border}`}>
                  <div className="flex items-center justify-between gap-4 flex-wrap">
                    <div className="flex items-center gap-4">
                      <span className={`grid place-items-center h-14 w-14 rounded-2xl bg-white/60 ${meta.color}`}><ScoreIcon size={28} /></span>
                      <div>
                        <p className={`text-lg font-bold ${meta.color}`} data-testid="sentiment-label">{sentiment.label}</p>
                        <p className="text-xs text-slate-500 mt-0.5">Diperbarui {fmtDateTime(sentiment.created_at)}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className={`font-mono text-4xl font-black ${meta.color}`} data-testid="sentiment-score">{pct}%</p>
                      <p className="text-xs text-slate-500">indeks positif</p>
                    </div>
                  </div>
                  <div className="mt-4">
                    <div className="h-2.5 w-full rounded-full bg-white/60">
                      <div className={`h-full rounded-full ${meta.bar} transition-all duration-700`} style={{ width: `${pct}%` }} />
                    </div>
                    <div className="flex justify-between mt-1 text-[10px] text-slate-400">
                      <span>Negatif</span><span>Netral</span><span>Positif</span>
                    </div>
                  </div>
                  <p className="mt-4 text-sm text-slate-700 leading-relaxed" data-testid="sentiment-summary">{sentiment.summary}</p>
                </div>

                <div className="grid gap-5 lg:grid-cols-2">
                  {/* Aspects */}
                  {sentiment.aspects?.length > 0 && (
                    <div className="clay p-6">
                      <p className="label-xs mb-4">Aspek Meeting</p>
                      <div className="space-y-3">
                        {sentiment.aspects.map((asp, i) => {
                          const m2 = SENTIMENT_META[asp.sentiment] || SENTIMENT_META.netral;
                          return (
                            <div key={i} className={`rounded-xl p-3.5 ${m2.bg} border ${m2.border}`}>
                              <div className="flex items-center justify-between mb-1">
                                <p className="text-sm font-semibold text-slate-800">{asp.aspect}</p>
                                <span className={`text-xs font-medium ${m2.color}`}>{m2.label}</span>
                              </div>
                              <p className="text-xs text-slate-600 leading-relaxed">{asp.description}</p>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Speaker sentiments */}
                  {sentiment.speaker_sentiments?.length > 0 && (
                    <div className="clay p-6">
                      <p className="label-xs mb-4">Sentimen Per Pembicara</p>
                      <div className="space-y-3">
                        {sentiment.speaker_sentiments.map((sp, i) => {
                          const m2 = SENTIMENT_META[sp.sentiment] || SENTIMENT_META.netral;
                          const spPct = Math.round(((parseFloat(sp.score || 0) + 1) / 2) * 100);
                          return (
                            <div key={i} className="flex items-center gap-3" data-testid={`speaker-sentiment-${i}`}>
                              <span className="grid place-items-center h-9 w-9 shrink-0 rounded-full grad-brand text-white text-[11px] font-bold">
                                {(sp.speaker || "?").slice(0, 2).toUpperCase()}
                              </span>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between mb-1">
                                  <p className="text-sm font-medium text-slate-800 truncate">{sp.speaker}</p>
                                  <span className={`text-xs font-semibold ${m2.color} shrink-0 ml-2`}>{m2.label}</span>
                                </div>
                                <div className="h-1.5 w-full rounded-full bg-slate-100">
                                  <div className={`h-full rounded-full ${m2.bar}`} style={{ width: `${spPct}%` }} />
                                </div>
                                {sp.key_phrases?.length > 0 && (
                                  <div className="flex flex-wrap gap-1 mt-1.5">
                                    {sp.key_phrases.slice(0, 3).map((kp, ki) => (
                                      <span key={ki} className="text-[10px] bg-slate-100 text-slate-500 rounded px-1.5 py-0.5">"{kp}"</span>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>

                {/* Highlights */}
                {(sentiment.highlights?.positive_moments?.length > 0 || sentiment.highlights?.tension_points?.length > 0) && (
                  <div className="grid gap-5 lg:grid-cols-2">
                    {sentiment.highlights.positive_moments?.length > 0 && (
                      <div className="clay p-5">
                        <div className="flex items-center gap-2 mb-3"><TrendingUp size={14} className="text-emerald-600" /><p className="label-xs text-emerald-700">Momen Positif</p></div>
                        <ul className="space-y-2">{sentiment.highlights.positive_moments.map((m, i) => (
                          <li key={i} className="flex gap-2 text-sm text-slate-700"><span className="text-emerald-500 mt-0.5">✓</span>{m}</li>
                        ))}</ul>
                      </div>
                    )}
                    {sentiment.highlights.tension_points?.length > 0 && (
                      <div className="clay p-5">
                        <div className="flex items-center gap-2 mb-3"><TrendingDown size={14} className="text-amber-600" /><p className="label-xs text-amber-700">Titik Ketegangan</p></div>
                        <ul className="space-y-2">{sentiment.highlights.tension_points.map((m, i) => (
                          <li key={i} className="flex gap-2 text-sm text-slate-700"><span className="text-amber-500 mt-0.5">!</span>{m}</li>
                        ))}</ul>
                      </div>
                    )}
                  </div>
                )}

                {/* Recommendations */}
                {sentiment.recommendations?.length > 0 && (
                  <div className="clay p-6">
                    <div className="flex items-center gap-2 mb-4"><Lightbulb size={14} className="text-violet-600" /><p className="label-xs text-violet-700">Rekomendasi AI</p></div>
                    <ul className="space-y-2.5">
                      {sentiment.recommendations.map((r, i) => (
                        <li key={i} className="flex gap-3 text-sm text-slate-700">
                          <span className="flex-shrink-0 h-5 w-5 rounded-full grad-brand text-white text-[10px] font-bold grid place-items-center mt-0.5">{i + 1}</span>
                          {r}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="flex justify-end">
                  <Button variant="outline" size="sm" className="rounded-xl border-slate-200" onClick={genSentiment} disabled={analyzingSentiment} data-testid="regen-sentiment-btn">
                    {analyzingSentiment ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Sparkles size={14} className="mr-2" />} Re-analisis
                  </Button>
                </div>
              </div>
            );
          })()}
        </TabsContent>
      </Tabs>

      <Dialog open={pplOpen} onOpenChange={setPplOpen}>
        <DialogContent className="max-w-2xl" data-testid="manage-participants-dialog">
          <DialogHeader><DialogTitle className="font-display">Kelola Peserta Rapat</DialogTitle></DialogHeader>
          <p className="-mt-2 text-sm text-slate-500">{picked.length} peserta dipilih. Peserta dengan voiceprint aktif akan dikenali otomatis.</p>
          {allUsers.length === 0 ? (
            <div className="py-10 text-center text-sm text-slate-400"><Loader2 size={18} className="mx-auto mb-3 animate-spin" /> Memuat daftar pengguna…</div>
          ) : (
            <ParticipantPicker
              users={allUsers}
              selected={picked}
              onToggle={(uid) => setPicked((p) => (p.includes(uid) ? p.filter((x) => x !== uid) : [...p, uid]))}
              testId="detail-participant-list"
            />
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setPplOpen(false)} className="rounded-xl" data-testid="cancel-participants-btn">Batal</Button>
            <Button className="rounded-xl grad-brand text-white" onClick={saveParticipants} disabled={savingPpl} data-testid="save-participants-btn">
              {savingPpl ? <Loader2 size={16} className="mr-2 animate-spin" /> : null} Simpan Peserta
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
